import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Recipe } from '@/types';
import { Lock, Clock, Users } from 'lucide-react';
import { formatTime } from '@/lib/utils';

interface UserProfileProps {
  userId: string;
}

interface Profile {
  username: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string;
  city?: string;
  country?: string;
  is_public: boolean;
  recipe_count: number;
  follower_count: number;
}

export default function UserProfile({ userId }: UserProfileProps) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileError) throw profileError;
      setProfile(profile);

      if (profile.is_public) {
        const { data: recipes, error: recipesError } = await supabase
          .from('recipes')
          .select('*')
          .eq('user_id', userId)
          .eq('is_public', true)
          .order('created_at', { ascending: false });

        if (recipesError) throw recipesError;
        setRecipes(recipes || []);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
        <p className="text-gray-500">This user profile does not exist.</p>
      </div>
    );
  }

  if (!profile.is_public) {
    return (
      <div className="text-center py-12">
        <Lock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Private Profile</h2>
        <p className="text-gray-500">This user's profile is private.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-primary-600">
              {profile.username.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold">{profile.username}</h1>
            {profile.display_name && (
              <p className="text-gray-600">{profile.display_name}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{profile.recipe_count}</div>
            <div className="text-sm text-gray-500">Recipes</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{profile.follower_count}</div>
            <div className="text-sm text-gray-500">Followers</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">
              {recipes.reduce((total, recipe) => total + recipe.servings, 0)}
            </div>
            <div className="text-sm text-gray-500">Total Servings</div>
          </div>
        </div>

        {profile.bio && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">About</h3>
            <p className="text-gray-900">{profile.bio}</p>
          </div>
        )}

        {(profile.city || profile.country) && (
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Location</h3>
            <p className="text-gray-900">
              {[profile.city, profile.country].filter(Boolean).join(', ')}
            </p>
          </div>
        )}
      </div>

      <div>
        <h2 className="text-xl font-bold mb-6">Public Recipes</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.map((recipe) => (
            <div
              key={recipe.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {recipe.imageUrl ? (
                <img
                  src={recipe.imageUrl}
                  alt={recipe.name}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-primary-50 flex items-center justify-center">
                  <Users className="h-12 w-12 text-primary-300" />
                </div>
              )}
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-3">{recipe.name}</h3>
                
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary-500" />
                    <span>{formatTime(recipe.prepTime + recipe.cookTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary-500" />
                    <span>{recipe.servings} servings</span>
                  </div>
                </div>

                {recipe.tags && recipe.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {recipe.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-primary-50 text-primary-700 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {recipes.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Public Recipes</h3>
            <p className="text-gray-500">
              This user hasn't shared any recipes yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}